import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material'

interface AppErrorBoundaryProps {
  children: ReactNode
}

interface AppErrorBoundaryState {
  hasError: boolean
  errorMessage: string | null
}

export default class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      errorMessage: null
    }
  }

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    const errorMessage = error instanceof Error ? error.message : 'Произошла непредвиденная ошибка интерфейса.'
    return {
      hasError: true,
      errorMessage
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('Unhandled UI render error:', error, errorInfo)
    }
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: 'background.default' }}>
        <Paper elevation={2} sx={{ p: 3, maxWidth: 560, width: '100%' }}>
          <Stack spacing={2}>
            <Typography variant="h5">Интерфейс временно недоступен</Typography>
            <Alert severity="error">
              {this.state.errorMessage || 'Произошла непредвиденная ошибка интерфейса.'}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Попробуйте перезагрузить страницу. Если ошибка повторяется, сообщите администратору.
            </Typography>
            <Box>
              <Button variant="contained" onClick={this.handleReload}>
                Перезагрузить страницу
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>
    )
  }
}
